import React, { useContext, useState } from 'react';
import { Animated, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { parseISO, isBefore } from 'date-fns';
import PropTypes from 'prop-types';
import { EventGridItem } from './EventGridItem';
import ComponentTitle from '../../common/ComponentTitle';
import { Grid } from '../../common/Grid';
import IconButton from '../../common/IconButton';
import LayoutContext from '../../../contexts/Layout';
import { H5 } from '../../text';

const EventGrid = ({
    fullCalendarLink,
    fullCalendarLinkText,
    campus,
    events = [],
    followContentLink,
    initialLoadCount,
    ministry,
    nodeTypeAlias = 'EventsGrid',
    references,
    title,
    width,
    hardWidth = 12.345
}) => {
    const { layout } = useContext(LayoutContext);
    const [pageIndex, setPageIndex] = useState(0);

    const AnimatedTouchableOpacity = Animated.createAnimatedComponent(TouchableOpacity);

    if (events?.length === 0) {
        return null;
    }

    const renderHeader = filteredCount => {
        const handlePage = isBack => {
            if (isBack) {
                if (pageIndex - 1 < 0) {
                    setPageIndex(0);
                } else {
                    setPageIndex(pageIndex - 1);
                }
            } else {
                const totalPages = Math.ceil(events.length / initialLoadCount);

                if (pageIndex + 1 > totalPages) {
                    setPageIndex(totalPages);
                } else {
                    setPageIndex(pageIndex + 1);
                }
            }
        };

        const renderBackBtn = () => {
            if (events?.length > initialLoadCount) {
                return (
                    <IconButton
                        disabled={pageIndex === 0}
                        icon={<MaterialCommunityIcons color={pageIndex === 0 ? '#dde4e9' : '##153d6e'} name='chevron-left-circle-outline' size={26} />}
                        onPress={() => handlePage(true)}
                        style={{ margin: 10, paddingHorizontal: 0, paddingVertical: 0 }}
                    />
                );
            }

            return null;
        };

        const renderForwardBtn = () => {
            if (events?.length > initialLoadCount) {
                const totalPages = Math.ceil(events.length / initialLoadCount);

                return (
                    <IconButton
                        disabled={pageIndex + 1 === totalPages}
                        icon={<MaterialCommunityIcons color={pageIndex + 1 < totalPages ? '#153d6e' : '#7f868b'} name='chevron-right-circle-outline' size={26} />}
                        onPress={() => handlePage(false)}
                        style={{ margin: 0, paddingHorizontal: 0, paddingVertical: 0 }}
                    />
                );
            }

            return null;
        };

        return (
            <View style={{ alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row', marginBottom: layout.styleGuide.spacer2 }}>
                <View style={{ justifyContent: 'flex-start' }}>
                    <ComponentTitle style={{ padding: 0 }} title={title} />
                </View>
                <View style={{ justifyContent: 'flex-end' }}>
                    <View
                        style={StyleSheet.flatten(Styles.headerNavContainer, {
                            right: filteredCount < initialLoadCount ? layout.styleGuide.spacer1 : undefined
                        })}
                    >
                        {layout.device.isMobile && (
                            <IconButton
                                icon={<MaterialCommunityIcons color='#153d6e' name='calendar-month' size={24} />}
                                onPress={async () => await followContentLink(false, fullCalendarLink.internalKey, undefined, references)}
                            />
                        )}
                        {!layout.device.isMobile && fullCalendarLinkText && fullCalendarLink?.internalKey && (
                            <AnimatedTouchableOpacity
                                onPress={async () => await followContentLink(false, fullCalendarLink.internalKey, undefined, references)}
                                style={{ justifyContent: 'center' }}
                            >
                                <H5 style={Styles.calendarText} text={fullCalendarLinkText} />
                            </AnimatedTouchableOpacity>
                        )}
                        <View
                            style={{ flexDirection: 'row', marginRight: layout.styleGuide.spacer2 }}
                            dataSet={{ spacerRight: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                        >
                            {filteredCount > initialLoadCount && renderBackBtn()}
                            {filteredCount > initialLoadCount && renderForwardBtn()}
                        </View>
                    </View>
                </View>
            </View>
        );
    };

    const filteredEvents = events.filter(event => {
        let include = true;
        const eventStartDate = typeof event.startDateAndTime === 'string' ? parseISO(event.startDateAndTime) : new Date(event.startDateAndTime);

        if (isBefore(eventStartDate, new Date())) {
            include = false;
        } else if (!event.featuredImage.cdnImagePath || event.featuredImage.cdnImagePath.length === 0) {
            include = false;
        } else {
            if (campus.length > 0 && ministry.length > 0) {
                campus.forEach(p_campus => {
                    if (Array.isArray(event.campus) && event.campus.includes(p_campus)) {
                        ministry.forEach(p_ministry => {
                            if (Array.isArray(event.ministry) && !event.ministry.includes(p_ministry)) {
                                include = false;
                            }
                        });
                    }
                });
            }

            if (include) {
                if (ministry.length > 0) {
                    ministry.forEach(filter => {
                        if (Array.isArray(event.ministry) && !event.ministry.includes(filter)) {
                            include = false;
                        }
                    });
                }

                if (campus.length > 0) {
                    campus.forEach(filter => {
                        if (Array.isArray(event.campus) && !event.campus.includes(filter)) {
                            include = false;
                        }
                    });
                }
            }
        }

        return include;
    });

    if (filteredEvents?.length === 0) {
        return null;
    }

    const gridEvents =
        Array(Math.ceil(filteredEvents.length / initialLoadCount))
            .fill()
            .map((_, index) => index * initialLoadCount)
            .map(begin => filteredEvents.slice(begin, begin + initialLoadCount)) || [];

    if (gridEvents?.length === 0) {
        return null;
    }

    const gridItems = gridEvents[pageIndex].map((item, index) => {
        const { key, enableImageOverlay, ...itemProps } = item;

        return <EventGridItem {...itemProps} enableImageOverlay={enableImageOverlay} index={index} width={hardWidth} />;
    });

    return (
        <View
            style={{ marginBottom: layout.styleGuide.componentSeparator }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            {renderHeader(gridEvents.length)}
            <Grid id='event-grid' items={gridItems} width={hardWidth} />
        </View>
    );
};

const Styles = StyleSheet.create({
    calendarText: {
        color: '#153d6e',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '18pt' : 18,
        fontWeight: 'bold',
        lineHeight: Platform.OS.equals('web') ? '20pt' : 20
    },
    headerNavContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-end'
    }
});

EventGrid.propTypes = {
    fullCalendarLink: PropTypes.shape({
        externalUrl: PropTypes.string,
        internalKey: PropTypes.string,
        isExternal: PropTypes.bool
    }),
    fullCalendarLinkText: PropTypes.string,
    campus: PropTypes.array,
    events: PropTypes.array,
    followContentLink: PropTypes.func,
    initialLoadCount: PropTypes.number,
    ministry: PropTypes.array,
    references: PropTypes.object,
    title: PropTypes.string.isRequired,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default EventGrid;
